/*
  The icon layer, and the one defect it exists to fix.

  MEASURED: `×` appears 26 times in this client and means TWO DIFFERENT THINGS. Seven are dismiss
  controls — remove this transfer, delete this TSA, drop this level. The other nineteen are the
  multiplication operator, in prose and in formulas: "loaded cost = salary × multiplier", "Σ FTE ×
  dedication", "months × monthly base salary". `TsaPanel` carries both within forty lines of each
  other. A reader scanning that panel meets the same character as a verb and as an operator on one
  screen, and nothing distinguishes them.

  The reverse drift is there too: `✕` and `×` are different characters doing the same job in
  different files, because nobody could see the other one while writing.

  WHAT THIS DOES NOT TOUCH: the nineteen multiplication signs. `×` is the correct character for
  multiplication and replacing it with anything else would be worse typography. Only the CONTROLS
  move, which is what makes the remaining ones unambiguous — after this, a `×` on screen is always an
  operator and never a button.

  THE NAME IS REQUIRED, BY THE TYPE. `label` is not optional on `IconButton`, so a control whose
  entire content is a picture cannot be written without saying what it does. That is a compile error
  rather than a review comment, which matters because the old glyph buttons carried their names on
  `title` — an attribute that is easy to leave off, invisible to a keyboard user until they hover,
  and unreliable on touch. Every one of them happened to have it; nothing made them.

  WHY lucide: it is already in the tree, vetted by `tools/license-check.cjs` at P9a (ISC), and it is
  a named-export-per-icon ES module, so the bundle carries the handful of icons named below rather
  than the thousand it ships.
*/
import type { ReactNode } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  MessageSquare,
  Plus,
  RotateCcw,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react';

/*
  THE VOCABULARY, keyed by MEANING rather than by picture.

  A call site writes `icon={ICON.remove}`, not `icon={X}`, and that is the whole point of the
  indirection. The drift this milestone fixes was two characters doing one job — `×` in four files
  and `✕` in three — which happened because nobody writing one could see the other. A meaning-keyed
  map makes "what do we use for remove" a question with one place to look and one possible answer.

  It is also the list. Anybody can read the icon set of this product in six lines, which is the
  property that stops it growing to forty near-synonyms.
*/
export const ICON = {
  /** Dismiss, delete, drop, unpick. Never multiplication — see the header. */
  remove: X,
  add: Plus,
  moveUp: ArrowUp,
  moveDown: ArrowDown,
  /** Undo a local edit back to the stored value. Not the audit trail's undo, which is a command. */
  revert: RotateCcw,
  /**
   * The assistant, wherever it is reachable from.
   *
   * A SPEECH BUBBLE RATHER THAN A SPARKLE, deliberately, and the register argument is the same one the
   * dock's placement makes: this is a conversation with something that answers in words and raises
   * proposals a person reads. A sparkle would promise magic, which is exactly the expectation the
   * "nothing takes effect until somebody applies it" sentence spends its whole length undoing.
   */
  assistant: MessageSquare,
  /**
   * Destroy a stored record, as distinct from `remove`.
   *
   * `remove` is dismissal — drop this row from a list somebody is composing, and nothing outlives the
   * gesture. This is a delete that reaches the database and does not come back, and the two reading
   * identically on screen is how somebody clears a conversation meaning to close it.
   */
  destroy: Trash2,
  /**
   * Put a section away, or bring it back. `PanelSection` only.
   *
   * ONE ENTRY, ROTATED, rather than a chevron-right/chevron-down pair. Two entries would be two keys
   * for one meaning — the exact drift this map exists to stop — and it would put the open and closed
   * states in different files from each other: the picture here, the decision at the call site. A
   * CSS rotation also gives the movement for free, which a swap cannot.
   */
  disclosure: ChevronRight,
} satisfies Record<string, LucideIcon>;

/**
 * An icon, always decorative.
 *
 * `aria-hidden` on every one of them, without an escape hatch: an icon that is the only content of a
 * control gets its name from the control (see `IconButton`), and an icon beside text would otherwise
 * be read out twice. There is no case in this product where the picture itself is the message.
 */
export function Icon({
  as: Glyph,
  size = 14,
  className,
}: {
  as: LucideIcon;
  /** 14 matches the 13px body text's cap height. 16 for a control that stands alone. */
  size?: number;
  className?: string;
}): JSX.Element {
  return (
    <span className={className} aria-hidden="true" style={{ display: 'inline-flex' }}>
      {/*
        1.75 rather than lucide's default 2. At 14px a 2px stroke reads heavier than the 13px text
        beside it, and the icons looked like a different, bolder typeface embedded in the sentence.
      */}
      <Glyph size={size} strokeWidth={1.75} />
    </span>
  );
}

/**
 * A button whose content is an icon, which therefore MUST say what it does.
 *
 * `label` becomes `aria-label` and `title` both: the first is the accessible name, the second is the
 * hover text a sighted mouse user gets. They are deliberately the same string — two hand-written
 * descriptions of one control drift, and the version a screen reader hears is the one nobody
 * proof-reads.
 */
export function IconButton({
  icon,
  label,
  testId,
  disabled,
  className,
  onClick,
  children,
}: {
  icon: LucideIcon;
  /** Required. What the control does, in the words a reader would use. */
  label: string;
  testId?: string;
  disabled?: boolean;
  className?: string;
  onClick(): void;
  /** For the rare control that wants a word beside the picture. */
  children?: ReactNode;
}): JSX.Element {
  return (
    <button
      className={className ? `icon-button ${className}` : 'icon-button'}
      data-testid={testId}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon as={icon} />
      {children}
    </button>
  );
}
